import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StoreState } from "./store.state";
import { StoreProductsData } from "./store.types";
import {
  getStoreProducts,
  getStoreSeller,
  getStoreFullData,
  toggleStoreFollow,
} from "./store.thunk";

const storeInitialState: StoreState = {
  seller: null,
  sections: [],
  tabs: [],
  search: null,
  products: [],
  pagination: null,
  status: "idle",
  sellerStatus: "idle",
  followStatus: "idle",
  error: null,
  sellerError: null,
  followError: null,
  currentSlug: null,
};

const storeSlice = createSlice({
  name: "store",
  initialState: storeInitialState,
  reducers: {
    resetStore: (state) => {
      state.seller = null;
      state.sections = [];
      state.tabs = [];
      state.search = null;
      state.products = [];
      state.pagination = null;
      state.status = "idle";
      state.sellerStatus = "idle";
      state.followStatus = "idle";
      state.error = null;
      state.sellerError = null;
      state.followError = null;
      state.currentSlug = null;
    },
    // SSR mağaza sekmesi ürünlerini mount'ta yükler (client fetch flicker'ını önler).
    loadInitialStoreProducts: (
      state,
      action: PayloadAction<{ data: StoreProductsData; slug: string }>,
    ) => {
      // Status guard'ı YOK: sekme geçişinde store state'i artık sıfırlanmıyor
      // (bkz. StoreTabPageClient), o yüzden status önceki sekmeden "success"
      // kalıyor. Guard olsaydı yeni sekmenin SSR ürünleri hiç yüklenmez ve
      // eski sekmenin ürünleri ekranda kalırdı. Çağıran taraf zaten mount başına
      // bir kez tetikliyor (StoreAllProducts/StoreDealsProducts ssrSeededRef).
      state.products = action.payload.data.items;
      state.pagination = action.payload.data.pagination;
      // seller YAZILMAZ — ürünler endpoint'inin seller nesnesi kısmi ve farklı
      // şekilde (bkz. StoreProductsSeller). Buraya yazılınca sekme geçişlerinde
      // reset() sonrası header kısmi veriyle render edilip puan + "Doğrulanmış"
      // rozeti kayboluyor, getStoreFullData dönünce geri geliyordu.
      state.currentSlug = action.payload.slug;
      state.status = "success";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStoreSeller.pending, (state) => {
        state.sellerStatus = "loading";
        state.sellerError = null;
      })
      .addCase(getStoreSeller.fulfilled, (state, action) => {
        state.sellerStatus = "success";
        state.seller = action.payload;
        state.currentSlug = action.payload.slug;
      })
      .addCase(getStoreSeller.rejected, (state, action) => {
        state.sellerStatus = "error";
        state.sellerError = action.payload ?? "Bilinmeyen hata.";
      })
      .addCase(getStoreFullData.pending, (state) => {
        state.sellerStatus = "loading";
        state.sellerError = null;
      })
      .addCase(getStoreFullData.fulfilled, (state, action) => {
        state.sellerStatus = "success";
        state.seller = action.payload.data.seller;
        state.sections = action.payload.data.sections;
        state.tabs = action.payload.data.tabs;
        state.search = action.payload.data.search;
        state.currentSlug = action.payload.slug;
      })
      .addCase(getStoreFullData.rejected, (state, action) => {
        state.sellerStatus = "error";
        state.sellerError = action.payload ?? "Bilinmeyen hata.";
      })
      .addCase(getStoreProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getStoreProducts.fulfilled, (state, action) => {
        state.status = "success";
        // Ürünler API'sinden gelen seller kısmi (bkz. StoreProductsSeller) —
        // tam seller yalnızca /stores/{slug}'dan gelir. Buradan sadece gerçekten
        // dolu gelen alanları merge et; is_followed payload'da hiç olmayabilir,
        // koşulsuz atanırsa takip durumu undefined'a düşer.
        const incoming = action.payload.data.seller;
        if (state.seller) {
          if (incoming.is_followed !== undefined) {
            state.seller.is_followed = incoming.is_followed;
          }
          state.seller.follower_count = incoming.follower_count;
        }
        state.pagination = action.payload.data.pagination;
        state.currentSlug = action.payload.slug;

        const incomingPage = action.payload.data.pagination.current_page;
        if (incomingPage === 1) {
          state.products = action.payload.data.items;
        } else {
          state.products = [...state.products, ...action.payload.data.items];
        }
      })
      .addCase(getStoreProducts.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Bilinmeyen hata.";
      })
      .addCase(toggleStoreFollow.pending, (state) => {
        state.followStatus = "loading";
        state.followError = null;
      })
      .addCase(toggleStoreFollow.fulfilled, (state, action) => {
        state.followStatus = "success";
        if (state.seller) {
          state.seller.is_followed = action.payload.is_following;
          state.seller.follower_count = action.payload.follower_count;
        }
      })
      .addCase(toggleStoreFollow.rejected, (state, action) => {
        state.followStatus = "error";
        state.followError = action.payload ?? "Bilinmeyen hata.";
      });
  },
});

export const { resetStore, loadInitialStoreProducts } = storeSlice.actions;
export default storeSlice.reducer;
